import { spawn } from "node:child_process";

export interface RunResult {
  ok: boolean;
  code: number | null;
  stdout: string;
  stderr: string;
}

/**
 * A binary name safe to hand to `spawn` with `shell: false`.
 *
 * `shell: false` already keeps `spawn` from invoking a shell, so classic
 * metacharacter injection (`;`, `|`, backticks, `$()`) is not reachable
 * through it. This check is a second, explicit boundary: every caller in this
 * codebase passes a literal binary name (`wrangler`, `vercel`, `npx`, ...), so
 * a `cmd` containing a path separator, whitespace, or a shell metacharacter is
 * rejected before it reaches `spawn` rather than trusted implicitly.
 */
const SAFE_COMMAND = /^[a-zA-Z0-9][a-zA-Z0-9._-]*$/;

function isSafeCommand(cmd: string): boolean {
  return SAFE_COMMAND.test(cmd);
}

/** Run a command, capturing output. Never throws — inspect `.ok`. */
export function run(
  cmd: string,
  args: string[],
  opts: { cwd?: string; timeoutMs?: number; stdin?: string } = {},
): Promise<RunResult> {
  if (!isSafeCommand(cmd)) {
    return Promise.resolve({
      ok: false,
      code: null,
      stdout: "",
      stderr: `refusing to run unsafe command: ${cmd}`,
    });
  }
  return new Promise((resolve) => {
    const child = spawn(cmd, args, { cwd: opts.cwd, shell: false });
    // Secret values are piped in rather than passed as argv, so they never
    // appear in the process table or in shell history.
    if (opts.stdin !== undefined) {
      child.stdin?.on("error", () => {
        /* closed early — the close handler reports the real failure */
      });
      child.stdin?.end(opts.stdin);
    }
    let stdout = "";
    let stderr = "";
    let settled = false;
    const finish = (result: RunResult) => {
      if (settled) return;
      settled = true;
      resolve(result);
    };
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (opts.timeoutMs && opts.timeoutMs > 0) {
      timer = setTimeout(() => {
        try {
          child.kill("SIGTERM");
        } catch {
          /* ignore */
        }
        finish({
          ok: false,
          code: null,
          stdout,
          stderr: stderr || `timeout after ${opts.timeoutMs}ms`,
        });
      }, opts.timeoutMs);
    }
    child.stdout?.on("data", (d) => (stdout += d.toString()));
    child.stderr?.on("data", (d) => (stderr += d.toString()));
    child.on("error", () => {
      if (timer) clearTimeout(timer);
      finish({ ok: false, code: null, stdout, stderr });
    });
    child.on("close", (code) => {
      if (timer) clearTimeout(timer);
      finish({ ok: code === 0, code, stdout, stderr });
    });
  });
}

/** Run a command inheriting stdio (for long, user-visible tasks like installs/builds). */
export function runInherit(cmd: string, args: string[], opts: { cwd?: string } = {}): Promise<RunResult> {
  if (!isSafeCommand(cmd)) {
    return Promise.resolve({
      ok: false,
      code: null,
      stdout: "",
      stderr: `refusing to run unsafe command: ${cmd}`,
    });
  }
  return new Promise((resolve) => {
    const child = spawn(cmd, args, { cwd: opts.cwd, shell: false, stdio: "inherit" });
    child.on("error", () => resolve({ ok: false, code: null, stdout: "", stderr: "" }));
    child.on("close", (code) => resolve({ ok: code === 0, code, stdout: "", stderr: "" }));
  });
}

/** True if a binary is resolvable on PATH. */
export async function which(bin: string): Promise<boolean> {
  const probe = process.platform === "win32" ? "where" : "which";
  const r = await run(probe, [bin]);
  return r.ok && r.stdout.trim().length > 0;
}
